<template>
  <div class="overflow-hidden bg-white lg:border-gray-300 lg:shadow lg:border lg:rounded-md">
    <!-- start of TW header -->
    <div class="w-full overflow-hidden border-t sm:border-b">
      <div class="flex items-center justify-between">
        <!-- Conditions -->
        <div
          v-if="conditions"
          class="w-full text-center"
          :class="[closedToday ? 'text-tvap-pink bg-gray-50' : 'text-tvap-green bg-green-100']"
        >
          <div class="py-2 lg:py-3">
            <p
              v-for="(condition, idx) in conditions.split('.')"
              :key="idx"
              class="px-4 py-1 text-xs font-extrabold sm:text-sm"
            >
              {{ condition | fmtCondition }}
            </p>
          </div>
          <section
            v-if="!closedToday"
            class="block px-6 py-4 space-y-2 text-xs italic font-semibold text-center text-gray-700 bg-white"
          >
            <p>
              We suggest a <span class="underline">per child</span> contribution of £14 for a single session, and £20
              for a double session.
            </p>
            <p>
              However, cost should not be something that prevents you from using TVAP so do feel free to discuss with
              us, in confidence, if this is an issue for you.
            </p>
          </section>
        </div>

        <div v-else class="w-full">
          <h3 class="w-full text-sm font-bold leading-6 text-center text-gray-900 xs:text-base sm:text-lg">
            Check if we have availability
          </h3>
          <p class="w-full mt-1 text-xs font-semibold leading-5 text-center text-gray-800 xs:text-sm md:text-base">
            To begin your enquiry, let us know when you would like to visit
          </p>
        </div>
      </div>
    </div>
    <!-- end of TW header -->
    <hr class="block sm:hidden" />
    <div class="pt-2 bg-white">
      <section class="text-gray-800">
        <div class="pt-1 pb-4">
          <div class="flex flex-col items-center justify-center w-full">
            <!-- date -->
            <div class="flex flex-col items-center">
              <label for="date" class="block text-xs font-semibold leading-5 sm:text-sm">Date of visit</label>
              <div
                class="relative border border-gray-300 rounded shadow-sm focus:ring focus:ring-red-300 focus:outline-none overflow-hiddenx md:rounded-md sm:mt-1"
              >
                <input
                  id="date"
                  v-model="booking.date"
                  type="date"
                  :min="today"
                  name="date"
                  class="block w-full p-2 text-xs font-medium bg-white sm:text-sm sm:leading-5"
                  placeholder="dd/mm/yyyy"
                  @blur="dateEntry"
                  @change="dateEntry"
                />
              </div>
            </div>

            <div class="relative flex flex-col items-center justify-between w-full px-4">
              <!-- Sessions -->
              <div v-if="conditions && !closedToday" class="flex flex-col">
                <label class="block py-2 text-xs font-semibold leading-5 text-center sm:text-sm" for="sessions"
                  >How long would you like to visit for?</label
                >
                <div class="flex flex-col justify-center sm:items-start sm:space-x-8 sm:flex-row">
                  <div class="flex flex-col justify-center">
                    <span class="text-sm font-semibold tracking-wide text-center text-gray-600 underline"
                      >Single sessions</span
                    >
                    <ul name="singles" class="mt-1 space-y-2">
                      <li
                        v-for="(blocks, idx) in single"
                        :key="`single-${idx}`"
                        class="block mx-auto text-xs font-medium form-input sm:text-sm sm:leading-5 justify-self-center"
                      >
                        <div class="flex justify-center">
                          <input
                            :ref="`input-single-${idx}`"
                            type="radio"
                            name="session"
                            @click="choose(single[idx])"
                          />
                          <span class="ml-3">From</span><span class="ml-1">{{ blocks[0].time }}</span>
                          <span class="ml-1">to</span><span class="ml-1">{{ blocks[1].time }}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div class="flex flex-col justify-center mt-3 sm:mt-0">
                    <span class="text-sm font-semibold tracking-wide text-center text-gray-600 underline"
                      >Double sessions</span
                    >
                    <ul name="doubles" class="mt-1 space-y-2">
                      <li
                        v-for="(blocks, idx) in double"
                        :key="`double-${idx}`"
                        class="text-xs font-medium form-input sm:text-sm sm:leading-5"
                      >
                        <div class="flex justify-center">
                          <input
                            :ref="`input-double-${idx}`"
                            type="radio"
                            name="session"
                            @click="choose(double[idx])"
                          />
                          <span class="ml-3">From </span><span class="ml-1">{{ blocks[0].time }}</span>
                          <span class="ml-1">&nbsp;to&nbsp;</span><span class="ml-1">{{ blocks[1].time }}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Visitors -->
              <div
                v-if="conditions && !closedToday"
                class="flex flex-col items-center mt-2 sm:justify-center sm:flex-row sm:mt-4 sm:w-full"
              >
                <!-- Playground Users -->
                <div class="flex flex-col items-center sm:w-1/4">
                  <label for="users" class="block text-xs font-semibold leading-5 sm:text-sm">
                    <div class="relative flex items-center justify-center w-full pb-1">
                      <span>Playground Users</span>
                      <info-dropdown class="-right-6">
                        Please let us know how many children or adults with special needs will be coming with you.
                      </info-dropdown>
                    </div>
                  </label>
                  <div class="relative rounded-md shadow-sm">
                    <div class="flex items-center py-0">
                      <select
                        aria-label="Playground Users"
                        class="h-full px-6 py-2 mx-2 text-xs bg-white border border-transparent border-gray-300 rounded md:rounded-md sm:text-sm sm:leading-5"
                        @change="selectedNumber"
                      >
                        <option v-for="(no, idx) in [1, 2, 3, 4, 5, 6]" id="`users-${idx}`" :key="idx" name="users">
                          {{ no }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Carers (note this input is swallowed - merely here as a visual distinction to users)-->
                <div class="flex flex-col items-center mt-1 md:mt-0 sm:w-1/4">
                  <label for="carers" class="block text-xs font-semibold leading-5 sm:text-sm">
                    <div class="relative flex items-center justify-center w-full pb-1">
                      <span>Carers</span>
                    </div>
                  </label>
                  <div class="relative rounded-md shadow-sm">
                    <div class="flex items-center py-0">
                      <select
                        aria-label="Carers"
                        class="h-full px-6 py-2 mx-2 text-xs bg-white border border-transparent border-gray-300 rounded md:rounded-md sm:text-sm sm:leading-5"
                      >
                        <option v-for="(no, idx) in [1, 2, 3, 4, 5, 6]" :key="idx">
                          {{ no }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!available" class="flex items-center justify-center mt-4 mb-2 text-tvap-pink opacity-80">
            <span class="text-sm font-semibold tracking-wide">Sorry, no availability on this date</span>
          </div>

          <div
            class="flex flex-col items-center justify-center flex-shrink-0 pb-2 md:flex-row"
            :class="[!available ? 'mt-0' : 'mt-6']"
          >
            <div class="relative flex flex-col items-center justify-center w-1/2 text-center md:flex-row">
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white border border-transparent rounded-md focus:outline-none focus:shadow-outline"
                :class="activeButton"
                :disabled="!canCheck"
                @click="check"
              >
                Check availability
              </button>
              <a
                href="#"
                class="relative self-center mt-4 text-xs font-semibold underline opacity-100 text-tvap-pink hover:opacity-80 md:absolute md:right-10 md:ml-6 md:mt-0"
                @click.prevent="cancel"
                >cancel</a
              >
            </div>
          </div>
        </div>
      </section>
    </div>
    <landing-modal
      v-if="current.matches('enquiring.landing')"
      @continue="landingContinue"
      @cancel="cancel"
    ></landing-modal>
  </div>
</template>

<script>
// eslint-disable-next-line
import bookingController from '@/controllers/booking-controller';
import InfoDropdown from '@/components/global/forms/info-dropdown';
import LandingModal from '@/components/landing-modal';
import moment from 'moment';
import { slotsForDate } from '@/lib/sessions';
import stateProps from '@/components/mixins/state-props';

export default {
  filters: {
    fmtCondition(condition) {
      return condition ? `${condition}.` : '';
    },
  },

  components: { InfoDropdown, LandingModal },

  mixins: [stateProps],

  data() {
    return {
      available: true,
      booking: {
        date: '',
        users: 1,
        arrive: { id: 0, time: '' },
        leave: { id: 0, time: '' },
      },
      closedToday: false,
      conditions: '',
      single: [], // for display purposes only
      double: [], // for display purposes only
      sessions: null,
      today: moment().format('YYYY-MM-DD'),
      visitDate: null,
    };
  },

  computed: {
    activeButton() {
      return this.canCheck
        ? 'bg-tvap-pink opacity-100 hover:opacity-80'
        : 'bg-tvap-pink opacity-50 pointer-events-none';
    },

    canCheck() {
      return !this.closedToday && !!this.booking.date && !!this.booking.arrive.time && !!this.booking.leave.time;
    },
  },

  methods: {
    cancel() {
      this.$service.send('LOGOFF');
    },

    async check() {
      const { date, users, arrive, leave } = this.booking;

      if (!arrive.time || !leave.time || !date || !users || !moment(date, 'YYYY-M-D').isValid()) return;

      // Check against Salesforce - returning false == no availability for that
      // number on the date requested.
      this.available = await bookingController.availability(this.booking);

      if (this.available) {
        this.$service.send('RESOLVE', { booking: this.booking });
      } else {
        this.$service.send('REJECT');
      }
    },

    choose(session) {
      // nothing to choose if there are no sessions on the chosen day
      if (!Array.isArray(this.sessions) || this.sessions.length < 1) return;

      this.booking.arrive.id = session[0].id;
      this.booking.arrive.time = session[0].time;
      this.booking.leave.id = session[1].id;
      this.booking.leave.time = session[1].time;
    },

    /**
     * Special code to allow the app to be used with restricted time slots and
     * not rewriting everything.
     * Simply return true if the timeslot is blocked
     */

    dateEntry(ev) {
      // switch off error message if it's there after previous failed check
      this.available = true;

      // disable the submit button, switch off all radio buttons & reset initial state
      this.booking.arrive.id = 0;
      this.booking.arrive.time = '';
      this.booking.leave.id = 0;
      this.booking.leave.time = '';
      Object.keys(this.$refs)
        .filter(k => k.startsWith('input-') && this.$refs[k].length)
        .map(k => this.$refs[k][0])
        .forEach(input => (input.checked = false));
      this.booking.users = 1;

      const dt = moment(ev.target.value);
      this.visitDate = dt.format('YYYY-MM-DD');

      // get back a nested array of all sessions for the given date
      const { slots, conditions } = slotsForDate(this.visitDate);
      this.sessions = slots;

      this.single = slots.filter(slot => slot[1].id - slot[0].id <= 6);
      this.double = slots.filter(slot => slot[1].id - slot[0].id > 6);

      // TVAP sometimes opens on holidays i.e. days that would not normally be
      // open.
      // In thoses cases, the text and closed status from Salesforce needs to be
      // ignored and replaced with values from the slotsForDate function
      if (conditions) {
        this.closedToday = false;
        this.conditions = conditions;
      } else {
        const dayno = Number(dt.format('E'));
        const day = this.current.context.environment.filter(d => Number(d.dow) === dayno);
        this.closedToday = day[0].disabled;
        this.conditions = day[0].conditions;
      }
    },

    landingContinue() {
      this.$service.send('RESOLVE');
    },

    selectedNumber(ev) {
      this.booking.users = ev.target.value;
    },
  },
};
</script>
